import { ReactNode, ComponentProps, useMemo } from "react";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import type { ActionItemGroup } from "@/lib/Components/Actions/ActionGroupButtons";
import { ActionMenuItem } from "@/lib/Components/Actions/ActionMenuItem";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";

interface GenericMenuProps {
  button: ReactNode;
  actionGroups: ActionItemGroup[];
  children?: ReactNode;
}

//Wrap the button, so we can prevent the default action when click on the button to close
function MyCustomButton({ onClick, ...props }: ComponentProps<"button">) {
  return (
    <button
      onClick={(e) => {
        onClick?.(e);
        e.preventDefault();
      }}
      {...props}
    />
  );
}

export function ActionMenu({
  button,
  actionGroups,
  children,
}: GenericMenuProps) {
  const { isAdmin } = useTenant();

  const filteredActionGroups = useMemo(() => {
    return actionGroups
      .map((group) => ({
        ...group,
        actions: group.actions.filter((action) => {
          if (action.isAdminAction && !isAdmin) return false;
          if (action.isHidden) return false;
          return true;
        }),
      }))
      .filter((group) => group.actions.length > 0);
  }, [actionGroups, isAdmin]);

  return (
    <Menu as="div" className="inline-block">
      <MenuButton as={MyCustomButton}>{button}</MenuButton>
      <Transition
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems
          anchor={{
            to: "bottom end",
            gap: 10,
          }}
          className="z-100 w-56 origin-top-right rounded-md border border-gray-200 bg-white shadow-lg outline-none"
        >
          <div className="divide-y divide-gray-100">
            {filteredActionGroups.length === 0 ? (
              <MenuItem disabled>
                <span className="data-disabled:opacity-50 block cursor-not-allowed p-2">
                  No actions available
                </span>
              </MenuItem>
            ) : null}

            {filteredActionGroups.map(({ actions }, itemIndex) => {
              return (
                <div key={itemIndex} className="py-1">
                  {actions.map((action, index: number) => (
                    <ActionMenuItem {...action} key={index} />
                  ))}
                </div>
              );
            })}
            {children}
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  );
}
